import React from 'react';
import { SwitchToggle } from 'src/shared/forms/components';
import type { ProductWizardData } from '../../../hooks/useProductWizard';

interface PreOrderSettingsSectionProps {
  preOrder: ProductWizardData['shipping']['preOrder'];
  onPreOrderChange: (preOrder: ProductWizardData['shipping']['preOrder']) => void;
}

export const PreOrderSettingsSection: React.FC<PreOrderSettingsSectionProps> = ({
  preOrder,
  onPreOrderChange,
}) => {
  const handlePreOrderToggle = () => {
    onPreOrderChange({
      ...(preOrder || { type: 'PRE_ORDER' as const, enabled: false }),
      enabled: !preOrder?.enabled,
      shipInDays: !preOrder?.enabled ? preOrder?.shipInDays : undefined,
      type: 'PRE_ORDER' as const,
    });
  };

  const handleShipInDaysChange = (days: number) => {
    onPreOrderChange({
      ...(preOrder || { type: 'PRE_ORDER' as const, enabled: false }),
      shipInDays: days,
      enabled: preOrder?.enabled ?? false,
      type: 'PRE_ORDER' as const,
    });
  };

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 md:tw-p-6 tw-shadow-sm">
      <div className="tw-mb-6 tw-border-b tw-border-gray-200 tw-pb-4">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900">
          Pre-Order Settings
        </h3>
        <p className="tw-text-sm tw-text-gray-500">
          Configure pre-order options for products not immediately available for
          shipping.
        </p>
      </div>
      <div className="tw-grid tw-gap-6">
        {/* Enable Pre-Order */}
        <SwitchToggle
          title="Enable Pre-Order"
          description="Allow customers to purchase before the product is ready to ship"
          checked={preOrder?.enabled || false}
          onChange={handlePreOrderToggle}
        />

        {/* Processing Time (Conditional) */}
        {preOrder?.enabled && (
          <div className="tw-transition tw-ease-out tw-duration-200">
            <div className="tw-flex tw-flex-col tw-gap-2">
              <label className="tw-text-sm tw-font-medium tw-text-gray-700">
                Processing Time <span className="tw-text-red-500">*</span>
              </label>
              <div className="tw-relative tw-max-w-xs">
                <input
                  value={preOrder?.shipInDays || ""}
                  onChange={(e) =>
                    handleShipInDaysChange(parseInt(e.target.value) || 0)
                  }
                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 tw-rounded-lg tw-px-4 tw-py-2.5 tw-pr-12 tw-text-gray-900 tw-placeholder-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                  placeholder="7"
                  type="number"
                  min="1"
                  max="365"
                  required={preOrder?.enabled}
                />
                <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3 tw-text-sm tw-text-gray-500">
                  days
                </div>
              </div>
              <p className="tw-text-xs tw-text-gray-500">
                Number of days needed to prepare and process the order before
                shipping (1-365 days)
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};